import { Icons } from "@codesandbox/template-icons";
import { Box, Button, Popover, Stack, styled, Tabs, Text } from "prism-react";
import { useUploadFile } from "queries/sandbox/api";
import React from "react";

import { TemplateIcon } from "components/TemplateIcon";

const HEIGHT = 180;
const VERTICAL_PADDING = 4;
const CONTENT_MARGIN = 16;

const IconButton = styled(Button, {
  display: "flex",
  margin: 0,
  padding: "0 !important",

  justifyContent: "center",
  alignItems: "center",
});

type UploadState =
  | {
      type: "ready";
    }
  | {
      type: "uploading";
    }
  | {
      type: "error";
      error: string;
    };

export const TemplatePicker: React.FC<{
  title: string;
  templateIconUrl: string | undefined;
  selectIcon: (iconUrl: string) => void;
}> = ({ title, templateIconUrl, selectIcon }) => {
  const fileRef = React.useRef<HTMLInputElement>(null);
  const [uploadState, setUploadState] = React.useState<UploadState>({
    type: "ready",
  });
  const uploadFileApi = useUploadFile();

  const buttonRef = React.useRef<HTMLButtonElement>(null);

  const selectIconAndClosePopover = (iconUrl: string) => {
    selectIcon(iconUrl);

    // Bit of a hack, but the controlled popover (by using rootProps) did not work. Because of this,
    // we close the popover by manually clicking the button again.
    if (buttonRef.current) {
      buttonRef.current.click();
    }
  };

  const uploadFile = async (name: string, content: string) => {
    setUploadState({ type: "uploading" });

    try {
      const { url } = await uploadFileApi(name, content);

      selectIconAndClosePopover(url);
      setUploadState({ type: "ready" });
    } catch (e) {
      if (typeof e === "object" && e != null) {
        const error = e as { response: { data?: { message: string } } };

        if ("response" in error) {
          if (error.response.data?.message) {
            setUploadState({
              type: "error",
              error: error.response.data.message,
            });
          }
        } else if ("message" in e) {
          const error = e as { message: string };
          setUploadState({ type: "error", error: error.message });
        }
      }
    }
  };

  return (
    <Popover
      contentProps={{ style: { height: "inherit" }, align: "start" }}
      trigger={
        <IconButton
          ref={buttonRef}
          css={{ span: { height: 24 } }}
          variant="ghost"
        >
          <TemplateIcon
            height={24}
            iconUrl={templateIconUrl}
            templateName={title}
            width={24}
          />
        </IconButton>
      }
    >
      <Box
        css={{
          width: 233,
          height: HEIGHT,
          paddingTop: VERTICAL_PADDING,
          paddingBottom: VERTICAL_PADDING,
        }}
      >
        <Tabs>
          <Tabs.List>
            <Tabs.Trigger css={{ fontWeight: 500 }} value="Preset">
              Icons
            </Tabs.Trigger>
            <Tabs.Trigger css={{ fontWeight: 500 }} value="Custom">
              Custom
            </Tabs.Trigger>
          </Tabs.List>

          <Box
            css={{
              marginTop: CONTENT_MARGIN,
              height: HEIGHT - CONTENT_MARGIN - VERTICAL_PADDING * 2,
              overflow: "scroll",
            }}
          >
            <Tabs.Content value="Preset">
              <Stack css={{ flexFlow: "wrap", gap: "$1" }}>
                {Object.keys(Icons).map((iconName) => {
                  // @ts-expect-error - iconName is a valid key
                  const Icon = Icons[iconName];

                  return (
                    <IconButton
                      key={iconName}
                      css={{ padding: "$1", span: { height: 21 } }}
                      onClick={() => selectIconAndClosePopover(iconName)}
                    >
                      <Icon height={21} width={21} />
                    </IconButton>
                  );
                })}
              </Stack>
            </Tabs.Content>

            <Tabs.Content value="Custom">
              <input
                ref={fileRef}
                accept="image/png, image/jpeg, image/jpg"
                onChange={(e) => {
                  if (!e.target.files) {
                    return;
                  }

                  const file = e.target.files[0];

                  if (!file) {
                    return;
                  }

                  const reader = new FileReader();

                  reader.onload = async () => {
                    if (typeof reader.result === "string") {
                      const [type, _content] = reader.result.split(",");

                      if (
                        type !== "data:image/png;base64" &&
                        type !== "data:image/jpeg;base64" &&
                        type !== "data:image/jpg;base64"
                      ) {
                        setUploadState({
                          type: "error",
                          error: "Only PNG and JPG files are supported",
                        });
                      } else {
                        uploadFile(file.name, reader.result);
                      }
                    }
                  };

                  reader.readAsDataURL(file);
                }}
                style={{ display: "none" }}
                type="file"
              />

              <Stack gap={4}>
                <Button
                  loading={uploadState.type === "uploading"}
                  onClick={() => {
                    if (fileRef.current) {
                      fileRef.current.click();
                    }
                  }}
                  variant="primary"
                >
                  Upload File
                </Button>

                {uploadState.type === "error" && (
                  <Text
                    css={{
                      color: "$informative-error-base",
                      textAlign: "center",
                    }}
                  >
                    {uploadState.error}
                  </Text>
                )}

                <Text css={{ textAlign: "center", lineHeight: "18px" }}>
                  Recommended size is 128 x 128. 5MB maximum.
                </Text>
              </Stack>
            </Tabs.Content>
          </Box>
        </Tabs>
      </Box>
    </Popover>
  );
};
